// stores/apiState.ts
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { friendlyMessage, validationMessages } from '@/helpers/customErrors.js';

export type TrackedKind = 'loading' | 'saving' | 'downloading';

export interface TrackOptions {
    /** Banner text shown when the call succeeds (saves, deletes, approvals …). */
    success?: string;
    /** Sentence used when the server sent no usable message. */
    error?: string;
    /** Handle the failure yourself (e.g. field-level errors) instead of the banner. */
    onError?: (error: unknown) => void;
    /** Offer "Try again" in the banner when this fails — on by default for loads. */
    retry?: boolean;
    /** Background lookups (typeahead searches, pickers): no banner while running, failures still shown. */
    quiet?: boolean;
}

/**
 * The one place the app's loading / saving / downloading / error / success
 * state lives (RCS-147). Every page renders it through <StatesComponent />
 * — no page draws its own spinner or keeps its own loading flag.
 *
 * Views call `track()` rather than toggling flags by hand:
 *
 *     await apiState.track('loading', async () => { rows.value = await api.list(); });
 *     await apiState.track('saving', async () => { await api.save(form); router.push(...); },
 *                          { success: 'Risk saved.' });
 *
 * Overlapping calls are counted, so one request finishing never clears the
 * flag another is still holding. track() never throws: a failure is shown
 * in the banner (plain language, see helpers/customErrors) and it resolves
 * to undefined — put the success path inside the callback. A failed load
 * leaves a "Try again" button in the banner that re-runs it.
 */
export const useApiState = defineStore('apiState', () => {
    // States to manage loading, error, success, etc.
    const loading = ref(false);
    const saving = ref(false);
    const error = ref(false);
    const errors = ref<string[]>([]);
    const downloading = ref(false);
    const success = ref(false);
    const message = ref('');
    /** Re-runs the call behind the current error banner ("Try again"), when there is one. */
    const retry = shallowRef<(() => unknown) | null>(null);

    const flags = { loading, saving, downloading };
    const inFlight: Record<TrackedKind, number> = { loading: 0, saving: 0, downloading: 0 };

    // Actions to trigger loading, saving, success, error, etc.
    const setLoading = (state: boolean) => {
        loading.value = state;
    };

    const setSaving = (state: boolean) => {
        saving.value = state;
    };

    const setError = (errorMessage: boolean) => {
        error.value = errorMessage;
    };

    const setSuccess = (state: boolean) => {
        success.value = state;
    };

    const setDownloading = (state: boolean) => {
        downloading.value = state;
    };

    const setMessage = (msg: string) => {
        message.value = msg;
    };


    const setErrors = (err: string[]) => {
        errors.value = err
    }

    /** Show a failure in the shared banner, in plain language. */
    const fail = (err: unknown, fallback?: string, again: (() => unknown) | null = null) => {
        success.value = false;
        errors.value = validationMessages(err);
        const summary = friendlyMessage(err, fallback);
        // Laravel's generic summary says nothing useful — lead with the first field's message.
        message.value = errors.value.length && /given data was invalid/i.test(summary) ? errors.value[0] : summary;
        retry.value = again;
        error.value = true;
    };

    /** Show a confirmation in the shared banner. */
    const succeed = (msg: string) => {
        error.value = false;
        errors.value = [];
        retry.value = null;
        message.value = msg;
        success.value = true;
    };

    async function track<T>(kind: TrackedKind, fn: () => Promise<T>, options: TrackOptions = {}): Promise<T | undefined> {
        const counted = !options.quiet;
        if (counted) {
            inFlight[kind]++;
            flags[kind].value = true;
        }
        if (kind !== 'loading' && counted) {
            error.value = false;
            errors.value = [];
        }
        retry.value = null;

        try {
            const result = await fn();
            if (options.success) succeed(options.success);
            return result;
        } catch (err) {
            if (options.onError) options.onError(err);
            else {
                const again = (options.retry ?? (kind === 'loading' && !options.quiet)) ? () => track(kind, fn, options) : null;
                fail(err, options.error, again);
            }
            return undefined;
        } finally {
            if (counted) {
                inFlight[kind] = Math.max(0, inFlight[kind] - 1);
                flags[kind].value = inFlight[kind] > 0;
            }
        }
    }

    // Reset all states
    const resetState = () => {
        inFlight.loading = inFlight.saving = inFlight.downloading = 0;
        loading.value = false;
        saving.value = false;
        error.value = false;
        downloading.value = false;
        success.value = false;
        message.value = '';
        errors.value = []
        retry.value = null;
    };

    return {
        loading,
        saving,
        error,
        success,
        message,
        errors,
        downloading,
        retry,
        setLoading,
        setSaving,
        setError,
        setErrors,
        setSuccess,
        setMessage,
        setDownloading,
        fail,
        succeed,
        track,
        resetState,
    };
});
